/**
 * Shared entry point for completing labour steps and services (#4101).
 *
 * `useWorkCompletionGate` owns the single "Työ valmis?" dialog of a view. Its
 * `requestCompletion` runs the completion straight away unless it is the one
 * that finishes the work order; then it opens the dialog, which runs the
 * completion after the reading, spot and service book are recorded.
 *
 * Deeply nested rows (LaborItemRow) reach the gate of the work-order view
 * through `WorkCompletionGateContext`; without a provider they complete
 * directly, as before.
 */

import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import { WorkCompletionDialog, type WorkCompletionWorkOrder } from './WorkCompletionDialog';
import {
  willCompleteWorkOrder,
  type WorkCompletionServiceSnapshot,
  type WorkCompletionTarget,
} from './workCompletion';

export type RequestWorkCompletion = (
  target: WorkCompletionTarget,
  complete: () => Promise<void>,
) => Promise<void>;

export const WorkCompletionGateContext = createContext<RequestWorkCompletion | null>(null);

/** The gate of the surrounding work-order view, or null outside one. */
export function useWorkCompletionRequest(): RequestWorkCompletion | null {
  return useContext(WorkCompletionGateContext);
}

export interface WorkCompletionGateWorkOrder extends WorkCompletionWorkOrder {
  status: string;
  services: readonly WorkCompletionServiceSnapshot[];
}

export interface WorkCompletionGate {
  requestCompletion: RequestWorkCompletion;
  dialog: ReactNode;
}

export function useWorkCompletionGate(
  workOrder: WorkCompletionGateWorkOrder | undefined,
): WorkCompletionGate {
  const [pending, setPending] = useState<{ complete: () => Promise<void> } | null>(null);
  const workOrderId = workOrder?.id;

  // A dialog from the previous work order must not complete a step on the next.
  useEffect(() => {
    setPending(null);
  }, [workOrderId]);

  const requestCompletion = useCallback<RequestWorkCompletion>(
    async (target, complete) => {
      if (workOrder && willCompleteWorkOrder(workOrder.status, workOrder.services, target)) {
        setPending({ complete });
        return;
      }
      await complete();
    },
    [workOrder],
  );

  // Mounted only while open, so its parking and mileage queries (the parking
  // status polls) run only when asked for, not once per card on My Tasks.
  const dialog = workOrder && pending ? (
    <WorkCompletionDialog
      open
      workOrder={workOrder}
      complete={pending.complete}
      onCancel={() => setPending(null)}
      onCompleted={() => setPending(null)}
    />
  ) : null;

  return { requestCompletion, dialog };
}
